import { GridPinnedColumnFields } from '@mui/x-data-grid-pro';
import { Dayjs, isDayjs } from 'dayjs';
import { DispatchFilterMap, DispatchLegColumns } from '../../Components/DataGrid/Dispatch/types';
import { FilterOperator, FilterValue } from '../../Components/Filters/types';
import { dayjsNow, parseDayJs } from '@/Infrastructure/Utilities/dayjs.type';

export enum LegType {
  BOB = 'Bobtail Leg',
  CHS = 'Chassis Leg',
  ECL = 'Empty Container Leg',
  FCL = 'Full Container Leg'
}

/** Only empty and full container legs carry a container; the API clears the container number on any other leg type. */
export const legTypeHasContainer = (legType: keyof typeof LegType | undefined) => legType === 'ECL' || legType === 'FCL';

/** Bobtail legs do not use a chassis; the API clears the chassis number on them. */
export const legTypeHasChassis = (legType: keyof typeof LegType | undefined) => legType !== 'BOB';

export const noContainerReason = 'Container is only available for ECL and FCL Legs';
export const noChassisReason = 'Chassis is not available for a Bobtail Leg';

export enum LegStatus {
  AC = 'Acknowledged',
  AV = 'Available',
  CL = 'Cleared',
  DC = 'Driver Check Call',
  DP = 'Dispatched',
  DL = 'Delivered',
  HB = 'Hubbed',
  MA = 'Matched',
  NA = 'Not Available',
  NW = 'New',
  OH = 'On Hold',
  OS = 'On Site',
  UN = 'Unknown',
  PU = 'Picked Up'
}

export interface MyProfile {
  /** Trinium Three-digit User Code */
  userCode: string;
  /** User Concatonated Name */
  name: string;
  /** Role */
  canModifyBoardQueries?: boolean;
  /** Role */
  canDeleteCharges?: boolean;
  /** Role */
  canDeleteDocuments?: boolean;
  /** Role */
  canHoldCharges?: boolean;
  /** Role */
  canHoldPayments?: boolean;
  /** Role */
  canViewAgtGrossProfit?: boolean;
  /** Role */
  canViewAgtMainChgProfit?: boolean;
  /** NFD Role */
  canViewNotFullyDelivered?: boolean;
  /** Profit Pay Approval Role */
  canViewProfitPayApproval?: boolean;
  /** EDI Role */
  canModifyLoadTenders?: boolean;
  /** EDI Role */
  canDeleteLoadTenders?: boolean;
  /** Agent Settlements Role */
  canViewAgtSettlements?: boolean;
  /** Terminal Preferences Role. Gates the Terminal Preferences item in the user avatar menu */
  canEditTerminalPreferences?: boolean;
  /** Global Settings */
  settings: GlobalSettings;
  /** Available Terminals to the current User */
  terminals: Terminal[];
}

interface GlobalSettings {
  /** Text template for the Customer components when the Customer has a Credit Warning */
  custCreditWrn: string;
  /** Flag indicating that the instance should allow Copying Charges and Legs */
  canCopyChargesAndLegs?: boolean;
}

export interface Terminal {
  number: string;
  name: string;
  scac: string;
  isActive: boolean;
  isDefault: boolean;
  incCustInfo: boolean;
  /** Terminal settings enabling the Copying of a Consignment  */
  canCopyConsignment?: boolean;
  /** Terminal settings controlling whether Invoice Charges action is visible */
  invChgVisible?: boolean;
  /** Flag indicating the terminal is setup for MC3 (UVI WEB-MC3-TERMINALS) */
  isMc3Terminal?: boolean;
  /** Flag indicating the terminal should ask whether a container/chassis change applies to only the current leg (UVI HORIZONCOPYCNTRBYTYPE and HORIZONCOPYCNTRBYTYPEEXCL) */
  promptForCntrChassisDup?: boolean;
  /** Transport mode from site maintenance (e.g. Ocean, Rail, Air, Road, Landbridge) */
  transportMode?: string;
  /** Flag indicating the terminal lands on a Power BI report instead of the Board */
  usePowerBISplash?: boolean;
  /** BCC email address for final charges */
  finalChargesBccEmailAddr?: string;
  /** Ignore the Are You Sure prompt when deleting records on the Charges and Payments DataGrids */
  disableDeletePaymentsWarning?: boolean;
  /** Payments grid: calculate the AP-FSC Pct over the payee's AP-DRAY payments instead of the linked AR-FSCP charges. Corporate rows are unaffected */
  useFscPctOfDray?: boolean;
  /** Flag indicating the terminal is a company store, which has no Agent Settlements */
  isCompanyStore?: boolean;
  /** Agent Supplier associated with the Terminal */
  agtId: string;
  agtName: string;
  agtAddress1: string;
  agtCity: string;
  agtState: string;
  agtZip: string;
}

export interface EquipmentNumber {
  prefix?: string;
  number: string;
  suffix?: string;
  /** Original Equipment Number before parsing */
  raw?: string;
}

/** Taking the API ISO Date string and parsing into useable fields for the application */
export interface ParsedDate {
  /** Unix timestamp of the ISO date to use for filtering comparisons */
  unix: number;
  /** MM/DD/YYYY */
  date: string;
  /** MM/DD */
  shortDate: string;
  /** MM/DD/YY */
  shortDateExtended: string;
  /** HH:mm */
  time: string;
  /** MM/DD/YY HH:mm */
  dateTime: string;
}

export interface ParsedTraceResult {
  message?: string;
  isSuccess?: boolean;
  timestamp?: string;
}

export function parsedDateNow(): ParsedDate {
  const now = dayjsNow();
  return {
    date: now.format('MM/DD/YYYY'),
    shortDate: now.format('MM/DD'),
    shortDateExtended: now.format('MM/DD/YY'),
    time: now.format('HH:mm'),
    dateTime: now.format('MM/DD/YY HH:mm'),
    unix: now.valueOf()
  };
}

/**
 * Builds a ParsedDate from a Dayjs object, unix timestamp (ms), or ISO date string.
 * @param input Dayjs object, unix timestamp in milliseconds, or ISO date string
 * @returns ParsedDate object, or undefined if the input is missing or invalid
 */
export function toParsedDate(input: Dayjs | number | string): ParsedDate;
export function toParsedDate(input?: Dayjs | number | string | null): ParsedDate | undefined;
export function toParsedDate(input?: Dayjs | number | string | null): ParsedDate | undefined {
  if (input === undefined || input === null) return undefined;

  const dayJsDate = isDayjs(input) ? input : parseDayJs(input);
  if (!dayJsDate?.isValid()) return undefined;

  return {
    date: dayJsDate.format('MM/DD/YYYY'),
    shortDate: dayJsDate.format('MM/DD'),
    shortDateExtended: dayJsDate.format('MM/DD/YY'),
    time: dayJsDate.format('HH:mm'),
    dateTime: dayJsDate.format('MM/DD/YY HH:mm'),
    unix: dayJsDate.valueOf()
  };
}

/**
 * Method for parsing the API ISO Date String into the Parsed Date type used by the application
 * @param inputDate ISO-8 Date String received from API
 * @returns ParsedDate object
 */
export const parseIsoDate = (inputDate?: string | null) => {
  if (!inputDate) return undefined;

  try {
    const dayJsDate = parseDayJs(inputDate);
    if (!dayJsDate) {
      return undefined;
    }

    return {
      date: dayJsDate.format('MM/DD/YYYY'),
      shortDate: dayJsDate.format('MM/DD'),
      shortDateExtended: dayJsDate.format('MM/DD/YY'),
      time: dayJsDate.format('HH:mm'),
      dateTime: dayJsDate.format('MM/DD/YY HH:mm'),
      unix: dayJsDate.valueOf()
    };
  } catch {
    console.log(`An error occurred in parseIsoDate method. Cannot read input date value ${inputDate}`);
  }

  return undefined;
};

const equipmentNumberRegex = /(?<prefix>[a-zA-Z]*)[\s]?(?<number>[0-9]{6})-?(?<suffix>[0-9])?/g;

export function parseEquipmentNumber(equipmentNumber?: string): EquipmentNumber | undefined {
  if (!equipmentNumber) {
    return undefined;
  }

  const matches = [...equipmentNumber.matchAll(equipmentNumberRegex)];
  if (matches.length > 0) {
    const groups = matches[0].groups;
    if (groups) {
      return {
        prefix: groups['prefix'],
        number: groups['number'],
        suffix: groups['suffix'],
        raw: equipmentNumber
      };
    }
  }

  return {
    number: equipmentNumber,
    raw: equipmentNumber
  };
}

export function parseTraceResult(trcResult?: string): ParsedTraceResult | undefined {
  if (!trcResult) return undefined;

  // Expected input "Trace" "{status}" "{date}" "{time}"
  const segments = trcResult.split(' ');
  if (segments.length === 4) {
    // Expected results we can parse
    return {
      message: `${segments[0]} ${segments[1]}`,
      timestamp: `${segments[2]} ${segments[3]}`,
      isSuccess: segments[1].toLowerCase() === 'success'
    };
  }

  // We can't parse, just return whatever we got
  return {
    message: trcResult
  };
}

export function parseTraceAvail(trcAvail?: string): string | boolean | undefined {
  // If we don't have any data, or it is the string 'false' return false
  if (!trcAvail || trcAvail.toLowerCase() === 'false') {
    return false;
  }
  // If it is the string 'true' return true
  if (trcAvail === 'true') {
    return true;
  }
  // Otherwise we cannot determine it to be a boolean.
  return trcAvail;
}

/**
 * Describes the Board Query definition
 */
export interface BoardQuery {
  /** Identifier */
  id: number;
  /** String displayed for selecting query */
  name: string;
  /** Definition of the Query */
  configuration: {
    /** Columns definitions on how to render the board */
    columns: (keyof typeof DispatchLegColumns)[];
    /** Columns that should be pinned to the left side of the board (Default pinned cols already included and not returned by API) */
    pinnedCols: GridPinnedColumnFields;
    /** Filters applied to the data for this query */
    filters: QueryFilter[];
    /** Default Sort Order for the data */
    sortOrder?: {
      column: keyof typeof DispatchLegColumns;
      isDesc: boolean;
    }[];
  };
  /** Flag indicating query is the default for user */
  isDefault?: boolean;
  /** Flag indicating favorite queries for user */
  isFavorite?: boolean;
  /** Flag indicating query is active for the Dispatch Board */
  isActive?: boolean;
}

/** Single Filter Definition received from the API */
export interface QueryFilter {
  /** String representation of the "Field" to filter. BoardFilterMap maps this string to the actual DispatchLeg field(s). */
  map: keyof DispatchFilterMap;
  /** Operation to execute on the field */
  operator: FilterOperator;
  /** Value to execute the above operation again when comparing */
  value: FilterValue;
}

/** TODO: This is still being designed/implemented on the back end. Temporary */
export interface TriniumApiError {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  errors?: Record<string, string[] | undefined>;
}

/** One page of a paged list endpoint. The API reports the page window in the X-Page / X-Page-Size / X-Has-More response headers rather than the body. */
export interface PagedResponse<T> {
  items: T[];
  hasMore: boolean;
}

export interface BaseApiRequest {
  terminalNumber: string;
}

export interface BasePutApiRequest<PutAction, PutRequestData> extends BaseApiRequest {
  action: PutAction;
  data: PutRequestData;
}

export interface StartsWithParameter {
  startsWith?: string;
}

export interface IsActiveParameter {
  isActive?: boolean;
}
